/**
 * AIDIA finance module.
 * Parses bank data (CSV, Excel .xlsx locally; PDF via the AI layer), auto-categorizes
 * income/expenses with editable keyword rules, and aggregates by period. No external libs:
 * .xlsx is unzipped with the browser's DecompressionStream.
 */
const _mod = (() => {
  // ---- Default categories ----
  const EXPENSE_CATEGORIES = [
    "Groceries", "Food & Dining", "Entertainment", "Education", "Transport", "Fuel",
    "Utilities", "Rent/Mortgage", "Health & Medical", "Insurance", "Shopping", "Clothing",
    "Travel", "Subscriptions", "Phone & Internet", "Personal Care", "Gifts & Donations",
    "Taxes", "Fees & Charges", "Home & Garden", "Kids", "Pets", "Fitness",
    "Cash Withdrawal", "Investments", "Loan Repayment", "Miscellaneous",
  ];
  const INCOME_CATEGORIES = [
    "Salary", "Business Income", "Interest", "Dividends", "Refunds",
    "Rental Income", "Gifts Received", "Government Benefits", "Other Income",
  ];
  const defaultCategories = () => ({ expense: [...EXPENSE_CATEGORIES], income: [...INCOME_CATEGORIES] });

  // ---- Built-in keyword → category rules ----
  const KEYWORDS = [
    // groceries
    [["woolworth", "coles", "aldi", "iga", "costco", "supermarket", "grocer", "safeway", "walmart", "kroger", "trader joe", "whole foods"], "Groceries"],
    // food & dining
    [["mcdonald", "kfc", "hungry jack", "burger", "pizza", "domino", "restaurant", "cafe", "coffee", "starbucks", "uber eats", "ubereats", "doordash", "deliveroo", "grubhub", "subway", "nando", "guzman", "dining", "bakery", "bar & grill"], "Food & Dining"],
    // entertainment
    [["netflix", "spotify", "disney", "cinema", "hoyts", "event cinema", "ticketmaster", "steam", "playstation", "xbox", "nintendo", "hbo", "prime video", "concert", "amc theat"], "Entertainment"],
    // subscriptions
    [["apple.com/bill", "google *", "icloud", "adobe", "microsoft", "dropbox", "notion", "openai", "anthropic", "subscription", "patreon"], "Subscriptions"],
    // transport
    [["uber", "lyft", "taxi", "cab", "opal", "myki", "metro", "transit", "train", "bus ", "toll", "parking", "linkt"], "Transport"],
    // fuel
    [["shell", "bp ", "caltex", "ampol", "mobil", "chevron", "exxon", "petrol", "fuel", "gas station", "7-eleven fuel"], "Fuel"],
    // utilities
    [["electric", "energy", "water corp", "gas bill", "agl", "origin energy", "utility", "council rates"], "Utilities"],
    // phone & internet
    [["telstra", "optus", "vodafone", "verizon", "at&t", "t-mobile", "comcast", "internet", "broadband", "mobile"], "Phone & Internet"],
    // rent / mortgage
    [["rent", "mortgage", "landlord", "real estate", "property mgmt"], "Rent/Mortgage"],
    // health
    [["pharmacy", "chemist", "medical", "doctor", "dental", "hospital", "clinic", "physio", "optometr"], "Health & Medical"],
    // insurance
    [["insurance", "geico", "allianz", "aami", "nrma", "medibank", "bupa"], "Insurance"],
    // shopping / clothing
    [["amazon", "ebay", "kmart", "target", "big w", "myer", "david jones", "best buy", "ikea"], "Shopping"],
    [["clothing", "fashion", "uniqlo", "h&m", "zara", "nike", "adidas", "cotton on"], "Clothing"],
    // travel
    [["airline", "qantas", "jetstar", "virgin", "emirates", "hotel", "airbnb", "booking.com", "expedia", "flight"], "Travel"],
    // education
    [["school", "university", "tuition", "course", "udemy", "coursera", "textbook", "student"], "Education"],
    // fitness
    [["gym", "fitness", "anytime", "f45", "yoga", "pilates"], "Fitness"],
    // personal care
    [["salon", "barber", "haircut", "spa", "beauty", "cosmetic"], "Personal Care"],
    // pets
    [["petbarn", "petstock", "vet ", "veterinary", "pet "], "Pets"],
    // fees
    [["fee", "charge", "interest charge", "atm fee", "overdraw", "surcharge"], "Fees & Charges"],
    // cash
    [["atm", "cash withdrawal", "withdrawal"], "Cash Withdrawal"],
    // donations
    [["donation", "charity", "gofundme", "red cross", "unicef"], "Gifts & Donations"],
    // taxes
    [["tax office", "ato ", "irs ", "taxation"], "Taxes"],
    // income
    [["salary", "payroll", "wage", "pay - ", "pay/"], "Salary"],
    [["interest earned", "interest paid", "credit interest"], "Interest"],
    [["dividend"], "Dividends"],
    [["refund", "reversal", "rebate"], "Refunds"],
    [["centrelink", "benefit", "pension"], "Government Benefits"],
  ];

  function categorize(description, type, rules = {}) {
    const d = (description || "").toLowerCase();
    // user rules first (learned from manual overrides)
    for (const key of Object.keys(rules)) {
      if (key && d.includes(key)) return rules[key];
    }
    for (const [keys, cat] of KEYWORDS) {
      if (keys.some((k) => d.includes(k))) {
        // only apply income categories to income and vice-versa where it matters
        const isIncomeCat = INCOME_CATEGORIES.includes(cat);
        if (type === "income" && isIncomeCat) return cat;
        if (type === "expense" && !isIncomeCat) return cat;
      }
    }
    return type === "income" ? "Other Income" : "Miscellaneous";
  }

  // ============================================================
  // CSV parsing
  // ============================================================
  function parseCSV(text) {
    const rows = [];
    let row = [], field = "", inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else inQuotes = false;
        } else field += c;
      } else if (c === '"') inQuotes = true;
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\r") { /* ignore */ }
      else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
      else field += c;
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    return rows.filter((r) => r.some((c) => c.trim() !== ""));
  }

  // ============================================================
  // XLSX parsing (unzip with DecompressionStream, then parse XML)
  // ============================================================
  const xlsxSupported = () => typeof DecompressionStream !== "undefined";

  async function inflateRaw(bytes) {
    const ds = new DecompressionStream("deflate-raw");
    const stream = new Response(new Blob([bytes]).stream().pipeThrough(ds));
    return new Uint8Array(await stream.arrayBuffer());
  }

  // Minimal ZIP reader using the central directory (handles stored + deflate).
  async function unzip(buffer) {
    const dv = new DataView(buffer);
    const bytes = new Uint8Array(buffer);
    // find End Of Central Directory (0x06054b50), scanning backwards
    let eocd = -1;
    for (let i = buffer.byteLength - 22; i >= 0; i--) {
      if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
    }
    if (eocd < 0) throw new Error("Not a valid .xlsx (no ZIP directory).");
    const count = dv.getUint16(eocd + 10, true);
    let off = dv.getUint32(eocd + 16, true);
    const dec = new TextDecoder();
    const files = {};
    for (let n = 0; n < count; n++) {
      if (dv.getUint32(off, true) !== 0x02014b50) break;
      const method = dv.getUint16(off + 10, true);
      const compSize = dv.getUint32(off + 20, true);
      const nameLen = dv.getUint16(off + 28, true);
      const extraLen = dv.getUint16(off + 30, true);
      const commentLen = dv.getUint16(off + 32, true);
      const localOff = dv.getUint32(off + 42, true);
      const name = dec.decode(bytes.subarray(off + 46, off + 46 + nameLen));
      // local header: skip its variable name/extra to reach data
      const lNameLen = dv.getUint16(localOff + 26, true);
      const lExtraLen = dv.getUint16(localOff + 28, true);
      const dataStart = localOff + 30 + lNameLen + lExtraLen;
      const comp = bytes.subarray(dataStart, dataStart + compSize);
      files[name] = method === 0 ? comp : await inflateRaw(comp);
      off += 46 + nameLen + extraLen + commentLen;
    }
    return files;
  }

  const colToIndex = (ref) => {
    const m = /^([A-Z]+)/.exec(ref);
    if (!m) return 0;
    let n = 0;
    for (const ch of m[1]) n = n * 26 + (ch.charCodeAt(0) - 64);
    return n - 1;
  };

  async function parseXLSX(buffer) {
    if (!xlsxSupported()) throw new Error("This browser can't read .xlsx here — export CSV instead.");
    const files = await unzip(buffer);
    const dec = new TextDecoder();
    // shared strings
    let shared = [];
    if (files["xl/sharedStrings.xml"]) {
      const doc = new DOMParser().parseFromString(dec.decode(files["xl/sharedStrings.xml"]), "application/xml");
      shared = [...doc.getElementsByTagName("si")].map((si) =>
        [...si.getElementsByTagName("t")].map((t) => t.textContent).join(""));
    }
    // first worksheet
    const sheetName = Object.keys(files).find((f) => /^xl\/worksheets\/sheet\d+\.xml$/.test(f));
    if (!sheetName) throw new Error("No worksheet found in the .xlsx.");
    const doc = new DOMParser().parseFromString(dec.decode(files[sheetName]), "application/xml");
    const rows = [];
    for (const rowEl of doc.getElementsByTagName("row")) {
      const cells = [];
      for (const c of rowEl.getElementsByTagName("c")) {
        const idx = colToIndex(c.getAttribute("r") || "A1");
        const type = c.getAttribute("t");
        const vEl = c.getElementsByTagName("v")[0];
        let val = "";
        if (type === "s" && vEl) val = shared[parseInt(vEl.textContent, 10)] || "";
        else if (type === "inlineStr") val = c.textContent || "";
        else if (vEl) val = vEl.textContent;
        cells[idx] = val;
      }
      for (let i = 0; i < cells.length; i++) if (cells[i] === undefined) cells[i] = "";
      rows.push(cells);
    }
    return rows.filter((r) => r.some((c) => String(c).trim() !== ""));
  }

  // ============================================================
  // Column mapping + row → transaction
  // ============================================================
  function guessColumns(header) {
    const h = header.map((x) => String(x).toLowerCase().trim());
    const find = (...names) => h.findIndex((c) => names.some((n) => c.includes(n)));
    return {
      date: find("date", "posted", "transaction date"),
      description: find("description", "narration", "details", "memo", "reference", "payee", "particulars", "transaction"),
      amount: find("amount", "value"),
      debit: find("debit", "withdrawal", "money out", "paid out"),
      credit: find("credit", "deposit", "money in", "paid in"),
    };
  }

  const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
  function parseAmount(v) {
    if (typeof v === "number") return v;
    const s = String(v).replace(/[^0-9.\-()]/g, "").replace(/\((.*)\)/, "-$1");
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
  }
  function parseDate(v) {
    if (v === "" || v == null) return null;
    // Excel serial number
    if (/^\d+(\.\d+)?$/.test(String(v).trim()) && Number(v) > 20000 && Number(v) < 80000) {
      const d = new Date(EXCEL_EPOCH + Math.round(Number(v)) * 86400000);
      return isoDate(d);
    }
    const s = String(v).trim();
    // yyyy-mm-dd or yyyy/mm/dd
    let m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/.exec(s);
    if (m) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`;
    // dd-mmm-yyyy / dd mmm yy
    m = /^(\d{1,2})[\s\-/]([A-Za-z]{3,})[\s\-/](\d{2,4})/.exec(s);
    if (m) {
      const mon = MONTHS[m[2].slice(0, 3).toLowerCase()];
      if (mon != null) return `${fullYear(m[3])}-${pad(mon + 1)}-${pad(m[1])}`;
    }
    // dd/mm/yyyy (day-first default)
    m = /^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})/.exec(s);
    if (m) return `${fullYear(m[3])}-${pad(m[2])}-${pad(m[1])}`;
    const d = new Date(s);
    return isNaN(d) ? null : isoDate(d);
  }
  const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
  const pad = (n) => String(n).padStart(2, "0");
  const fullYear = (y) => { const n = parseInt(y, 10); return n < 100 ? 2000 + n : n; };
  const isoDate = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

  /** Turn parsed rows + a column mapping into transactions. */
  function rowsToTransactions(rows, mapping, { institution = "", rules = {} } = {}) {
    const out = [];
    for (const r of rows) {
      const date = parseDate(r[mapping.date]);
      if (!date) continue;
      const description = String(r[mapping.description] ?? "").trim() || "(no description)";
      let amount, type;
      if (mapping.debit >= 0 || mapping.credit >= 0) {
        const debit = mapping.debit >= 0 ? Math.abs(parseAmount(r[mapping.debit])) : 0;
        const credit = mapping.credit >= 0 ? Math.abs(parseAmount(r[mapping.credit])) : 0;
        if (credit > 0) { amount = credit; type = "income"; }
        else if (debit > 0) { amount = debit; type = "expense"; }
        else continue;
      } else {
        const raw = parseAmount(r[mapping.amount]);
        if (raw === 0) continue;
        type = raw < 0 ? "expense" : "income";
        amount = Math.abs(raw);
      }
      out.push({
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
        date, description, amount, type,
        category: categorize(description, type, rules),
        institution,
        createdAt: Date.now(), updatedAt: Date.now(),
      });
    }
    return out;
  }

  // ============================================================
  // PDF text → transactions (offline, best-effort line parsing)
  // ============================================================
  const DATE_RE = /\b(\d{1,2}[\/\-.]\d{1,2}[\/\-.]\d{2,4}|\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[\s\-][A-Za-z]{3,9}[\s\-]\d{2,4})\b/;
  // amounts must have cents or a currency symbol to avoid matching plain integers / dates
  const AMOUNT_RE = /(?:[-+(]?\s?\$?\d{1,3}(?:,\d{3})+(?:\.\d{2})?|[-+(]?\s?\$?\d+\.\d{2})\)?(?:\s?(?:CR|DR))?/gi;

  function parseAmountToken(tok) {
    const cr = /CR\b/i.test(tok);
    const dr = /DR\b/i.test(tok);
    const neg = /^\s*[-(]/.test(tok) || tok.includes(")");
    const val = parseFloat(tok.replace(/[^0-9.]/g, "")) || 0;
    return { value: val, credit: cr, debit: dr || neg && !cr, negative: neg };
  }

  const INCOME_HINTS = /\b(cr|credit|deposit|salary|payroll|refund|reversal|interest|dividend|transfer in|received|payment received)\b/i;
  const EXPENSE_HINTS = /\b(dr|debit|withdrawal|purchase|payment|eftpos|pos |atm|fee|direct debit)\b/i;

  function parsePdfText(text, { rules = {} } = {}) {
    const out = [];
    for (const rawLine of text.split("\n")) {
      const line = rawLine.replace(/\s+/g, " ").trim();
      if (line.length < 6) continue;
      const dm = DATE_RE.exec(line);
      if (!dm) continue;
      const date = parseDate(dm[0]);
      if (!date) continue;
      const amtMatches = [...line.matchAll(AMOUNT_RE)];
      if (!amtMatches.length) continue;
      // First money token = transaction amount; a trailing one is usually the running balance.
      const first = amtMatches[0];
      const tok = parseAmountToken(first[0]);
      if (!tok.value) continue;
      // description = text between the date and the first amount
      const dateEnd = dm.index + dm[0].length;
      let description = line.slice(dateEnd, first.index).replace(/\s+/g, " ").trim();
      if (!description) description = line.replace(dm[0], "").replace(first[0], "").trim();
      description = description || "(no description)";
      // classify income vs expense
      let type;
      if (tok.credit) type = "income";
      else if (tok.debit) type = "expense";
      else if (INCOME_HINTS.test(line)) type = "income";
      else if (EXPENSE_HINTS.test(line)) type = "expense";
      else type = "expense"; // most statement lines are debits
      out.push({
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
        date, description, amount: Math.abs(tok.value), type,
        category: categorize(description, type, rules),
        institution: "", createdAt: Date.now(), updatedAt: Date.now(),
      });
    }
    return out;
  }

  // ============================================================
  // Period aggregation
  // ============================================================
  function isoWeek(d) {
    const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const day = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
    return `${t.getUTCFullYear()}-W${pad(week)}`;
  }
  function periodKey(dateStr, gran) {
    const d = new Date(dateStr + "T00:00:00");
    const y = d.getFullYear();
    switch (gran) {
      case "day": return dateStr;
      case "week": return isoWeek(d);
      case "fortnight": {
        const days = Math.floor((Date.UTC(y, d.getMonth(), d.getDate()) - Date.UTC(1970, 0, 1)) / 86400000);
        return "FN-" + Math.floor(days / 14);
      }
      case "month": return `${y}-${pad(d.getMonth() + 1)}`;
      case "quarter": return `${y}-Q${Math.floor(d.getMonth() / 3) + 1}`;
      case "annual": return `${y}`;
      default: return dateStr;
    }
  }
  function periodLabel(key, gran, sampleDate) {
    if (gran === "fortnight" && sampleDate) {
      const d = new Date(sampleDate + "T00:00:00");
      return "Fortnight of " + d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    }
    if (gran === "month") { const [y, m] = key.split("-"); return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" }); }
    if (gran === "day") return new Date(key + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    return key;
  }

  function inRange(t, range) {
    if (!range) return true;
    if (range.from && t.date < range.from) return false;
    if (range.to && t.date > range.to) return false;
    return true;
  }

  /** Group transactions into ordered periods with income/expense totals + category maps. */
  function aggregate(transactions, gran, range) {
    const buckets = new Map();
    for (const t of transactions) {
      if (!inRange(t, range)) continue;
      const key = periodKey(t.date, gran);
      if (!buckets.has(key)) buckets.set(key, { key, sample: t.date, income: 0, expense: 0, incomeByCat: {}, expenseByCat: {}, txns: [] });
      const b = buckets.get(key);
      if (t.date < b.sample) b.sample = t.date;
      b.txns.push(t);
      if (t.type === "income") { b.income += t.amount; b.incomeByCat[t.category] = (b.incomeByCat[t.category] || 0) + t.amount; }
      else { b.expense += t.amount; b.expenseByCat[t.category] = (b.expenseByCat[t.category] || 0) + t.amount; }
    }
    return [...buckets.values()]
      .sort((a, b) => (a.sample < b.sample ? -1 : 1))
      .map((b) => ({ ...b, label: periodLabel(b.key, gran, b.sample) }));
  }

  function categoryTotals(transactions, type, range) {
    const map = {};
    for (const t of transactions) {
      if (t.type !== type || !inRange(t, range)) continue;
      map[t.category] = (map[t.category] || 0) + t.amount;
    }
    return Object.entries(map).map(([category, amount]) => ({ category, amount })).sort((a, b) => b.amount - a.amount);
  }

  return {
    EXPENSE_CATEGORIES, INCOME_CATEGORIES, defaultCategories,
    parseCSV, parseXLSX, xlsxSupported, guessColumns, rowsToTransactions,
    parsePdfText, categorize, aggregate, categoryTotals, periodKey,
  };
})();

// --- ES module surface -------------------------------------------------
export const { EXPENSE_CATEGORIES, INCOME_CATEGORIES, defaultCategories, parseCSV, parseXLSX, xlsxSupported, guessColumns, rowsToTransactions, parsePdfText, categorize, aggregate, categoryTotals, periodKey } = _mod;
export default _mod;
